import { useState, useCallback, useRef } from "react";
import { uploadAsset, deleteAsset } from "../../services/assetsApi";
import { config } from "../../config/environment";
import { VIDEO_MIME_TYPES, VIDEO_EXTENSIONS, getAcceptString, hasValidExtension } from "../../utils/fileTypes";
import {
  Box,
  Button,
  Icon,
  Spinner,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { MdUpload, MdDelete, MdVideocam } from "react-icons/md";
import { useParams } from "react-router-dom";
import { useRoomStore } from "../../store";
import VideoErrorModal from "../Scene/VideoErrorModal";
import { flushEditorSave } from "../../pages/EditorPage";

const MAX_FILE_SIZE = config.upload.maxFileSize.wallVideo;
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function VideoUploadForWalls() {
  const { roomId } = useParams();
  const fileInputRef = useRef(null);

  const materialOptions = useRoomStore((s) => s.materialOptions);
  const setMaterialOptions = useRoomStore((s) => s.setMaterialOptions);
  const setMaterialType = useRoomStore((s) => s.setMaterialType);
  const setMaterialId = useRoomStore((s) => s.setMaterialId);

  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Get current video URL from material options
  const currentVideoUrl = materialOptions?.src || null;
  const currentAssetKey = materialOptions?.assetKey || null;

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  }, []);

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
    setErrorMessage(null);
  }, []);

  const handleFileSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type — using canonical VIDEO_MIME_TYPES from fileTypes.js
      const fileName = file.name.toLowerCase();
      const hasValidMime = VIDEO_MIME_TYPES.includes(file.type);
      const extOk = hasValidExtension(file.name, 'video');

      if (!hasValidMime && !extOk) {
        showError("Only MP4, MOV, and M4V videos are supported");
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        showError(
          `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB for wall videos.`
        );
        return;
      }

      setUploading(true);

      try {
        const result = await uploadAsset(file, { roomId });

        if (result.success) {
          const videoUrl = result.data.publicUrl || result.data.url;
          setMaterialOptions({
            ...materialOptions,
            src: videoUrl,
            assetKey: result.data.assetKey,
          });
          // Now that we have a video, switch to video material
          setMaterialType("video");
          setMaterialId("20");
          // Flush after React re-renders so the auto-save effect has queued
          // the debounced call with the new roomConfig (flush() is a no-op if
          // called synchronously here before the effect runs)
          setTimeout(() => flushEditorSave(), 0);
        } else {
          showError(result.error?.message || "Upload failed");
        }
      } catch (err) {
        showError(err.message || "Upload failed");
      } finally {
        setUploading(false);
        // Reset file input so same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [roomId, materialOptions, setMaterialOptions, setMaterialType, setMaterialId, showError]
  );

  const handleDelete = useCallback(async () => {
    // Clear the video and revert to basic material
    const clearVideo = () => {
      setMaterialOptions({
        ...materialOptions,
        src: null,
        assetKey: null,
      });
      // Revert to basic material (Dry Wall)
      setMaterialType("basic");
      setMaterialId("0");
    };

    if (!currentAssetKey) {
      clearVideo();
      return;
    }

    try {
      await deleteAsset(currentAssetKey, { roomId });
      clearVideo();
    } catch (err) {
      console.error("Failed to delete video asset:", err);
      // Still clear locally even if delete fails
      clearVideo();
    }
  }, [roomId, currentAssetKey, materialOptions, setMaterialOptions, setMaterialType, setMaterialId]);

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box mt={4}>
      <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
        Upload Video:
      </Text>

      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptString('video')}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {currentVideoUrl ? (
        <VStack spacing={3} align="stretch">
          {/* Video preview */}
          <Box
            position="relative"
            bg="gray.900"
            borderRadius="0"
            overflow="hidden"
            border="2px solid"
            borderColor="gray.600"
          >
            <video
              src={currentVideoUrl}
              style={{
                width: "100%",
                maxHeight: "150px",
                objectFit: "contain",
              }}
              muted
              loop
              autoPlay
              playsInline
            />
            <Box
              position="absolute"
              top={2}
              right={2}
              bg="blackAlpha.700"
              borderRadius="0"
              p={1}
            >
              <Icon as={MdVideocam} color="white" boxSize={4} />
            </Box>
          </Box>

          {/* Actions */}
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={triggerFileSelect}
              isLoading={uploading}
              loadingText="Uploading..."
              flex={1}
              bg="gray.700"
              _hover={{ bg: "gray.600" }}
              borderRadius="0"
            >
              <Icon as={MdUpload} mr={1} />
              Replace
            </Button>
            <Button
              size="sm"
              onClick={handleDelete}
              colorScheme="red"
              variant="outline"
              flex={1}
              borderRadius="0"
            >
              <Icon as={MdDelete} mr={1} />
              Remove
            </Button>
          </HStack>

          {/* Disclaimer */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            *"High efficiency" iPhone videos don't work on Chrome desktop. Use MP4 to make it visible on any browser.
          </Text>
        </VStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {/* Upload button */}
          <Button
            onClick={triggerFileSelect}
            isLoading={uploading}
            loadingText="Uploading..."
            size="md"
            height="80px"
            bg="gray.800"
            border="2px dashed"
            borderColor="gray.600"
            _hover={{ bg: "gray.700", borderColor: "gray.500" }}
            borderRadius="0"
          >
            <VStack spacing={1}>
              {uploading ? (
                <Spinner size="md" />
              ) : (
                <>
                  <Icon as={MdUpload} boxSize={6} />
                  <Text fontSize="sm">Upload Video</Text>
                </>
              )}
            </VStack>
          </Button>

          {/* Help text */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            MP4 up to {MAX_FILE_SIZE_MB}MB. "High efficiency" iPhone videos don't work on Chrome desktop.
          </Text>
        </VStack>
      )}

      {/* Error modal */}
      {showErrorModal && (
        <VideoErrorModal message={errorMessage} onClose={closeErrorModal} />
      )}
    </Box>
  );
}
